import { isCheatsEnabled } from '../../../Cheats';
import {
    SCORE_BOMB_CARD,
    SCORE_FOG_CARD,
    SCORE_VALUE_CARD,
    SCORE_WATER_CARD,
} from '../../../Constants/Score';
import { CardModifierType } from '../../../Prototype/Level/Card/CardModifierType';
import { CardStatus } from '../../../Prototype/Level/Card/CardStatus';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { getCardSuit, valueDiff } from '../../../Utility/CardsHelper';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { IGameState } from '../../Game/IGameState';
import CardStateHelper from '../CardStateHelper';
import CardStateModifierHelper from '../CardStateModifierHelper';
import { CardTask } from '../CardTasks';
import BaseModifierBehavior from '../ModifierBehavior/BaseModifierBehavior';
import BaseCardBehavior from './BaseCardBehavior';

export default class ValueCardBehavior extends BaseCardBehavior {
    public isPlayable(state: IGameState, currentCard: string): boolean {
        const cardStatus = CardStateHelper.getCardStatus(state, this.cardID);

        // If a value card is "in-play" it must match the current card (absolute
        // value difference of exactly 1) to be played.
        if (cardStatus === CardStatus.IN_PLAY) {
            return (
                super.isPlayable(state, currentCard) &&
                this.isMatch(state, currentCard)
            );
        }

        return super.isPlayable(state, currentCard);
    }

    public isMatch(state: IGameState, cardB: string): boolean {
        // CHEATS
        if (
            window &&
            (window as any).key &&
            (window as any).key.Shift === true &&
            isCheatsEnabled()
        )
            return true;
        //

        const cardAData = state.levelState.cardsState.cards[this.cardID];
        const cardBData = state.levelState.cardsState.cards[cardB];

        // If nothing to match against always match.
        if (!cardBData) {
            return state.levelState.cardsState.inStack.length === 0;
        }

        // If cardB is a joker always match.
        if (cardBData.type === CardType.joker) return true;

        if (
            cardAData.type === CardType.value &&
            cardBData.type === CardType.value
        ) {
            // Check card values are exactly 1 away from eachother (in either direction - wrapping around ACE -> TWO).
            if (valueDiff(cardAData.value, cardBData.value) === 1) {
                return true;
            }
        }

        if (
            cardAData.type === CardType.value &&
            cardBData.type === CardType.gold
        ) {
            if (getCardSuit(cardAData.value) === cardBData.suit) {
                return true;
            }
        }

        return false;
    }

    public play(state: IGameState) {
        const cardStatus = CardStateHelper.getCardStatus(state, this.cardID);

        switch (cardStatus) {
            case CardStatus.IN_PLAY:
                const modBehaviors = this.modifiersOverriding(state);

                // Modifiers can override the default 'play' method.
                if (modBehaviors.length > 0) {
                    // Modifiers are played instead.
                    for (let i = 0, n = modBehaviors.length; i < n; i++) {
                        state = CardStateModifierHelper.playModifier(
                            state,
                            modBehaviors[i].modifier,
                            modBehaviors[i].cardID
                        );
                    }
                } else {
                    // By default the value card is moved to folded cards (considered a 'valid' turn) and
                    // all modifiers are updated.
                    state = CardStateHelper.pushUndoHistory(
                        state,
                        this.cardID,
                        CardStatus.IN_PLAY
                    );
                    // If there is a "scoreable" modifier (bomb or fog),
                    // increase streak sequence without increase the streak score.
                    let cardScore = this.scoreableModifier(state);
                    if (cardScore > 0) {
                        state = CardStateHelper.increaseStreak(state, 0);
                    } else {
                        state = CardStateHelper.increaseStreak(
                            state,
                            SCORE_VALUE_CARD
                        );
                        cardScore = state.levelState.cardsState.streakScore;
                    }
                    state = CardStateHelper.addTurnScore(state, cardScore);

                    state = CardStateModifierHelper.updateModifiers(
                        state,
                        this.cardID
                    );
                    state = CardStateHelper.setCardFolded(state, this.cardID);
                    state = CardStateHelper.setStateTaskData(state, {
                        task: CardTask.MATCH,
                        cards: [this.cardID],
                        cardScore: cardScore,
                    });
                    state = CardStateHelper.updateCards(state);
                    state = CardStateModifierHelper.onCardPlayed(
                        state,
                        this.cardID
                    );
                    state = CardStateHelper.checkStreakBonus(state);

                    // Reset `noActionCardStreak`.
                    track('mNoActionCardStreak', 0, TrackMethod.STREAK_END);
                }
                break;
            case CardStatus.IN_STACK:
                state = CardStateHelper.pushUndoHistory(
                    state,
                    this.cardID,
                    CardStatus.IN_STACK
                );

                state = CardStateHelper.setCardFolded(state, this.cardID);
                state = CardStateHelper.setStateTaskData(state, {
                    task: CardTask.FOLD,
                    cards: [this.cardID],
                });
                state = CardStateModifierHelper.updateModifiers(
                    state,
                    this.cardID,
                    true
                );
                state = CardStateHelper.cancelStreak(state);

                track('mNoActionCardStreak', 1, TrackMethod.STREAK);
                if (CardStateHelper.getCardMatches(state).length === 0) {
                    track('mNoPossibleActionStreak', 1, TrackMethod.STREAK);
                }
                break;
            case CardStatus.FOLDED:
            case CardStatus.DISCARDED:
            case CardStatus.UNKNOWN:
        }

        return state;
    }

    private modifiersOverriding(state: IGameState): BaseModifierBehavior[] {
        return state.levelState.cardsState.cards[this.cardID].modifiers
            .map(m =>
                CardStateModifierHelper.getModifierBehavior(
                    state,
                    m,
                    this.cardID
                )
            )
            .filter(b => b.overrideCardPlay(state));
    }

    private scoreableModifier(state: IGameState): number {
        for (const modifier of state.levelState.cardsState.cards[this.cardID]
            .modifiers) {
            if (modifier.type === CardModifierType.bomb) {
                return SCORE_BOMB_CARD;
            } else if (modifier.type === CardModifierType.fog) {
                return SCORE_FOG_CARD;
            } else if (modifier.type === CardModifierType.water) {
                return SCORE_WATER_CARD;
            }
        }
    }
}
